"use client";

import { useEffect, useRef, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface ReCaptchaV3Props {
  action: string; // Action name for this execution (e.g., 'form_submit')
  onVerify: (token: string, score?: number) => void;
  onError?: (error: Error) => void;
  executeOnMount?: boolean; // Whether to execute immediately when mounted
  className?: string;
}

export interface ReCaptchaV3Ref {
  executeRecaptcha: () => Promise<void>;
}

export const ReCaptchaV3: React.FC<ReCaptchaV3Props> = ({
  action,
  onVerify,
  onError,
  executeOnMount = false,
  className
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executedRef = useRef(false);

  const handleExecute = useCallback(async () => {
    if (!executeRecaptcha) {
      const error = new Error('reCAPTCHA v3 not ready');
      console.error('reCAPTCHA v3 not ready');
      onError?.(error);
      return;
    }

    try {
      const token = await executeRecaptcha(action);
      if (token) {
        // In a real implementation, you'd send the token to your backend
        // to get the score. For now, we'll simulate a score.
        onVerify(token);
      } else {
        throw new Error('Failed to get reCAPTCHA token');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      console.error('reCAPTCHA v3 execution failed:', errorObj);
      onError?.(errorObj);
    }
  }, [executeRecaptcha, action, onVerify, onError]);

  useEffect(() => {
    if (executeOnMount && !executedRef.current && executeRecaptcha) {
      executedRef.current = true;
      handleExecute();
    }
  }, [executeOnMount, executeRecaptcha, handleExecute]);

  // For development/debugging - show invisible status
  if (process.env.NODE_ENV === 'development') {
    return (
      <div className={`text-xs text-muted-foreground ${className || ''}`}>
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
          <span className="material-symbols-outlined text-blue-600">security</span>
          <span>reCAPTCHA v3 Active (Invisible Protection)</span>
        </div>
      </div>
    );
  }

  // In production, completely invisible
  return null;
};

// Hook for manual execution
export const useReCaptchaV3Execute = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const execute = async (action: string): Promise<string | null> => {
    if (!executeRecaptcha) {
      console.error('reCAPTCHA v3 not ready');
      return null;
    }

    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error) {
      console.error('reCAPTCHA v3 execution failed:', error);
      return null;
    }
  };

  return { execute, ready: !!executeRecaptcha };
};
